import React, { useState, useEffect } from "react";

const SavingsGoalTimelineCalculator = () => {
  const [goalAmount, setGoalAmount] = useState(500000);
  const [currentSavings, setCurrentSavings] = useState(50000);
  const [monthlySavings, setMonthlySavings] = useState(10000);
  const [expectedReturn, setExpectedReturn] = useState(8);
  const [result, setResult] = useState({
    monthsRequired: "0",
    yearsRequired: "0.00",
    totalContribution: "0.00",
    interestEarned: "0.00",
    targetDate: "",
  });

  // Savings Goal Calculation Function
  const calculateSavingsGoal = () => {
    const goal = parseFloat(goalAmount);
    const current = parseFloat(currentSavings);
    const monthly = parseFloat(monthlySavings);
    const annualReturn = parseFloat(expectedReturn) / 100;
    const monthlyReturn = annualReturn / 12;

    if (isNaN(goal) || isNaN(current) || isNaN(monthly) || isNaN(annualReturn) || goal <= 0 || monthly <= 0) return;

    // If current savings already meet or exceed goal
    if (current >= goal) {
      setResult({
        monthsRequired: "0",
        yearsRequired: "0.00",
        totalContribution: current.toFixed(2),
        interestEarned: "0.00",
        targetDate: "Goal Already Met!",
      });
      return;
    }

    // Calculate months required using Future Value of Annuity formula
    // FV = PV(1+r)^n + PMT * [((1+r)^n - 1) / r]
    // Solving for n (months)
    
    let months = 0;
    let balance = current;
    let totalDeposits = current;
    
    // Iterative calculation
    while (balance < goal && months < 1200) { // Max 100 years
      balance = balance * (1 + monthlyReturn) + monthly;
      totalDeposits += monthly;
      months++;
    }

    const years = months / 12;
    const interestEarned = balance - totalDeposits;
    
    // Calculate target date
    const today = new Date();
    const targetDate = new Date(today);
    targetDate.setMonth(targetDate.getMonth() + months);
    const formattedDate = targetDate.toLocaleDateString('en-IN', { 
      month: 'long', 
      year: 'numeric' 
    });

    setResult({
      monthsRequired: months.toString(),
      yearsRequired: years.toFixed(2),
      totalContribution: totalDeposits.toFixed(2),
      interestEarned: interestEarned.toFixed(2),
      targetDate: formattedDate,
    });
  };

  // Run default calculation on first load
  useEffect(() => {
  calculateSavingsGoal();
}, [goalAmount, currentSavings, monthlySavings, expectedReturn]);


  const progressPercentage = goalAmount > 0 
    ? ((Number(currentSavings) / Number(goalAmount)) * 100).toFixed(1)
    : 0;

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <h1>Savings Goal Timeline Calculator</h1>
          <p>
            Calculate how long it will take to reach your savings goal with regular monthly contributions and compound interest
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="16"></line>
                  <line x1="8" y1="12" x2="16" y2="12"></line>
                </svg>
                Goal Details
              </h2>

              {/* Goal Amount */}
              <div className="input-group">
                <label>Savings Goal Amount</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={goalAmount}
                    onChange={(e) => setGoalAmount(e.target.value)}
                    className="calc-input"
                    placeholder="500000"
                  />
                </div>
                <div className="quick-select-group">
                  {[100000, 500000, 1000000, 2000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setGoalAmount(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 100000 ? (amount / 100000).toFixed(0) + 'L' : (amount / 1000).toFixed(0) + 'K'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Current Savings */}
              <div className="input-group">
                <label>Current Savings</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={currentSavings}
                    onChange={(e) => setCurrentSavings(e.target.value)}
                    className="calc-input"
                    placeholder="50000"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 50000, 100000, 200000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setCurrentSavings(amount)}
                      className="quick-select-btn secondary"
                    >
                      ₹{amount >= 100000 ? (amount / 100000).toFixed(0) + 'L' : amount === 0 ? '0' : (amount / 1000).toFixed(0) + 'K'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Monthly Savings */}
              <div className="input-group">
                <label>Monthly Savings</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={monthlySavings}
                    onChange={(e) => setMonthlySavings(e.target.value)}
                    className="calc-input"
                    placeholder="10000"
                  />
                </div>
                <div className="quick-select-group">
                  {[5000, 10000, 20000, 50000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setMonthlySavings(amount)}
                      className="quick-select-btn success"
                    >
                      ₹{amount >= 10000 ? (amount / 1000).toFixed(0) + 'K' : amount}
                    </button>
                  ))}
                </div>
              </div>

              {/* Expected Return */}
              <div className="input-group">
                <label>Expected Annual Return</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={expectedReturn}
                    onChange={(e) => setExpectedReturn(e.target.value)}
                    className="calc-input"
                    placeholder="8"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
              </div>

              <button onClick={calculateSavingsGoal} className="calculate-btn">
                Calculate Timeline
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Goal Summary
              </h2>

              <div className="result-card">
                {/* Time Required - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Time to Reach Goal</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.yearsRequired} Years
                  </div>
                  <div className="result-subtext">
                    ({result.monthsRequired} months)
                  </div>
                </div>

                {/* Target Date */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Target Date</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value" style={{fontSize: '1.75rem'}}>
                    {result.targetDate}
                  </div>
                </div>

                {/* Total Contribution */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Contribution</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalContribution).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Your total deposits
                  </div>
                </div>

                {/* Interest Earned */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Interest Earned</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.interestEarned).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    At {expectedReturn}% annual return
                  </div>
                </div>

                {/* Current Progress */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Current Progress</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Goal Progress</span>
                      <span className="breakdown-percentage">
                        {progressPercentage}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${Math.min(progressPercentage, 100)}%`,
                        }}
                      />
                    </div>
                    <div className="breakdown-header" style={{marginTop: '0.5rem'}}>
                      <span className="breakdown-label" style={{fontSize: '0.875rem'}}>
                        ₹{Number(currentSavings).toLocaleString("en-IN")} of ₹{Number(goalAmount).toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Goal Composition</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Your Contributions</span>
                      <span className="breakdown-percentage">
                        {((Number(result.totalContribution) / Number(goalAmount)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(Number(result.totalContribution) / Number(goalAmount)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Interest Growth</span>
                      <span className="breakdown-percentage purple">
                        {((Number(result.interestEarned) / Number(goalAmount)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill purple"
                        style={{
                          width: `${(Number(result.interestEarned) / Number(goalAmount)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>How Does the Savings Goal Timeline Calculator Work?</h3>
              <p>
                The savings goal calculator determines how long it will take to reach your financial 
                target by considering your current savings, monthly contributions, and expected returns. 
                It uses compound interest calculations to show how your money grows over time, helping 
                you plan realistic timelines for your financial goals like buying a home, car, or 
                building an emergency fund.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Formula Used:</h4>
            <div className="formula-display">
              FV = PV(1 + r)ⁿ + PMT × [((1 + r)ⁿ - 1) / r]
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">FV</div>
                <div className="formula-param-value">Future Value (Goal Amount)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">PV</div>
                <div className="formula-param-value">Present Value (Current Savings)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">PMT</div>
                <div className="formula-param-value">Monthly Payment (Savings)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">r</div>
                <div className="formula-param-value">Monthly Interest Rate</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">n</div>
                <div className="formula-param-value">Number of Months</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>How do I set a realistic savings goal?</h5>
              <p>
                Start by identifying your specific financial objective (home down payment, emergency 
                fund, vacation, etc.) and determine the exact amount needed. Consider your timeline, 
                current income, and expenses to calculate an achievable monthly savings amount. A good 
                rule of thumb is to save 20% of your income, but adjust based on your circumstances 
                and priorities. Break larger goals into smaller milestones to stay motivated.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is a realistic expected return rate?</h5>
              <p>
                Expected returns vary by investment type. Savings accounts offer 3-4%, fixed deposits 
                give 6-7%, debt mutual funds provide 7-9%, and equity mutual funds can return 10-12% 
                over the long term. For short-term goals (under 3 years), use conservative rates like 
                6-7%. For long-term goals (5+ years), 8-10% is reasonable with balanced investments. 
                Always account for inflation and taxation when planning.
              </p>
            </div>

            <div className="faq-item">
              <h5>Should I prioritize paying debt or saving?</h5>
              <p>
                Generally, pay off high-interest debt (credit cards, personal loans above 12%) first, 
                as the interest you save exceeds typical investment returns. However, maintain a small 
                emergency fund (₹25,000-50,000) even while paying debt. Once high-interest debt is 
                cleared, focus on building a 6-month emergency fund. Then balance between lower-interest 
                debt payments and long-term savings/investments.
              </p>
            </div>

            <div className="faq-item">
              <h5>How much should I keep in an emergency fund?</h5>
              <p>
                Financial experts recommend maintaining 6-12 months of essential expenses in an easily 
                accessible emergency fund. If you have a stable job and dual income, 6 months is 
                sufficient. Single-income households or those with variable income should aim for 9-12 
                months. Keep this fund in high-yield savings accounts or liquid mutual funds for quick 
                access during unexpected situations like job loss or medical emergencies.
              </p>
            </div>

            <div className="faq-item">
              <h5>What if I can't meet my monthly savings target?</h5>
              <p>
                Don't get discouraged - something is better than nothing. Start with whatever amount 
                you can manage consistently, even if it's small. Review your budget to find areas to 
                cut expenses. Consider additional income sources like freelancing. Automate your 
                savings to ensure consistency. As your income grows, gradually increase your monthly 
                contribution. The key is developing the habit and staying committed to your goal.
              </p>
            </div>

            <div className="faq-item">
              <h5>Should I adjust my goals based on the timeline?</h5>
              <p>
                Yes, timelines and goals should be flexible and reviewed regularly. If the calculator 
                shows an unrealistic timeline, either increase monthly savings, reduce the goal amount, 
                or choose higher-return investments. Life circumstances change - job changes, salary 
                increases, family additions - so revisit your goals every 6-12 months. Celebrate 
                milestones and adjust targets as needed to maintain motivation and achievability.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SavingsGoalTimelineCalculator;